/**
 * Session Types Service - Local-First CRUD Operations
 *
 * ISSUE_052: Local-first write path with pending mutations queue.
 *
 * Manages session types (수업 유형) per academy.
 * Each academy can define custom session types with:
 * - name, code, color
 * - capacity_limit (최대 인원)
 * - capacity_priority (우선순위 - 높을수록 먼저 적용)
 *
 * Write operations:
 * 1. Update IndexedDB cache immediately (optimistic)
 * 2. Enqueue mutation for Supabase push
 * 3. Trigger sync processor
 *
 * @module sessionTypesService
 */

import { supabase, isSupabaseConfigured } from "~/shared/lib/supabase";
import { mutateWithAuthRetry, paginatedQuery } from "~/core/api/executeWithAuthRetry";
import { SyncError } from "~/core/sync/SyncError";

// ============================================================================
// TYPES
// ============================================================================

export interface SessionType {
  id: string;
  academyId: string | null;
  ownerId: string | null;
  name: string;
  code: string;
  color: string;
  capacityLimit: number | null;
  capacityPriority: number;
  sortOrder: number;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSessionTypeInput {
  academyId?: string | null;
  ownerId?: string | null;
  name: string;
  code: string;
  color: string;
  capacityLimit?: number | null;
  capacityPriority?: number;
  sortOrder?: number;
  description?: string;
}

export interface UpdateSessionTypeInput {
  name?: string;
  code?: string;
  color?: string;
  capacityLimit?: number | null;
  capacityPriority?: number;
  sortOrder?: number;
  description?: string;
  isActive?: boolean;
}

// ============================================================================
// HELPERS
// ============================================================================

interface DbSessionType {
  id: string;
  academy_id: string | null;
  owner_id: string | null;
  name: string;
  code: string;
  color: string;
  capacity_limit: number | null;
  capacity_priority: number;
  sort_order: number;
  description: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

function fromDbFormat(db: DbSessionType): SessionType {
  const result: SessionType = {
    id: db.id,
    academyId: db.academy_id,
    ownerId: db.owner_id,
    name: db.name,
    code: db.code,
    color: db.color,
    capacityLimit: db.capacity_limit,
    capacityPriority: db.capacity_priority,
    sortOrder: db.sort_order,
    isActive: db.is_active,
    createdAt: db.created_at,
    updatedAt: db.updated_at,
  };
  if (db.description) {
    result.description = db.description;
  }
  return result;
}

function toDbInsertFormat(input: CreateSessionTypeInput, id: string) {
  return {
    id,
    academy_id: input.academyId ?? null,
    owner_id: input.ownerId ?? null,
    name: input.name,
    code: input.code,
    color: input.color,
    capacity_limit: input.capacityLimit ?? null,
    capacity_priority: input.capacityPriority ?? 50,
    sort_order: input.sortOrder ?? 0,
    description: input.description ?? null,
    is_active: true,
  };
}

// ============================================================================
// READ OPERATIONS
// ============================================================================

/**
 * Fetch only active session types (for use in pickers)
 * Supports both academy workspace (academyId) and personal workspace (ownerId)
 */
export async function fetchActiveSessionTypes(options: {
  academyId?: string | null | undefined;
  ownerId?: string | null | undefined;
}): Promise<{ data: SessionType[]; error?: string }> {
  if (!isSupabaseConfigured() || !supabase) {
    return { data: [], error: "Supabase not configured" };
  }

  const { academyId, ownerId } = options;

  // Must have at least one identifier
  if (!academyId && !ownerId) {
    return { data: [], error: "Either academyId or ownerId must be provided" };
  }

  try {
    const data = await paginatedQuery<DbSessionType>((from, to) => {
      let query = supabase!
        .from("session_types")
        .select("*")
        .eq("is_active", true)
        .order("sort_order", { ascending: true });

      if (academyId) {
        query = query.eq("academy_id", academyId);
      } else if (ownerId) {
        query = query.eq("owner_id", ownerId);
      }

      return query.range(from, to);
    });

    return { data: data.map(fromDbFormat) };
  } catch (err) {
    console.error("[sessionTypesService] Fetch active exception:", err);
    return { data: [], error: "Failed to fetch active session types" };
  }
}

// ============================================================================
// WRITE OPERATIONS — 순수 Supabase 호출 (서버 권위). 낙관/캐시는 React Query.
// ============================================================================

/** 수업유형 생성 — Supabase upsert (멱등: onConflict id). */
export async function createSessionTypeRemote(
  input: CreateSessionTypeInput,
  id: string,
): Promise<void> {
  if (!isSupabaseConfigured() || !supabase) {
    throw new Error("Supabase not configured");
  }
  const { error } = await mutateWithAuthRetry(() =>
    supabase!
      .from("session_types")
      .upsert(toDbInsertFormat(input, id), { onConflict: "id" })
      .select()
      .single(),
  );
  if (error) throw SyncError.fromSupabase(error);
}

/** 수업유형 수정 — Supabase update (camelCase → snake_case). */
export async function updateSessionTypeRemote(
  id: string,
  updates: UpdateSessionTypeInput,
): Promise<void> {
  if (!isSupabaseConfigured() || !supabase) {
    throw new Error("Supabase not configured");
  }
  const dbUpdates: Record<string, unknown> = {};
  if (updates.name !== undefined) dbUpdates["name"] = updates.name;
  if (updates.code !== undefined) dbUpdates["code"] = updates.code;
  if (updates.color !== undefined) dbUpdates["color"] = updates.color;
  if (updates.capacityLimit !== undefined) dbUpdates["capacity_limit"] = updates.capacityLimit;
  if (updates.capacityPriority !== undefined) dbUpdates["capacity_priority"] = updates.capacityPriority;
  if (updates.sortOrder !== undefined) dbUpdates["sort_order"] = updates.sortOrder;
  if (updates.description !== undefined) dbUpdates["description"] = updates.description;
  if (updates.isActive !== undefined) dbUpdates["is_active"] = updates.isActive;
  if (Object.keys(dbUpdates).length === 0) return;

  const { error } = await mutateWithAuthRetry(() =>
    supabase!.from("session_types").update(dbUpdates).eq("id", id),
  );
  if (error) throw SyncError.fromSupabase(error);
}

/** 수업유형 삭제 — soft delete (is_active=false). */
export async function deleteSessionTypeRemote(id: string): Promise<void> {
  if (!isSupabaseConfigured() || !supabase) {
    throw new Error("Supabase not configured");
  }
  const { error } = await mutateWithAuthRetry(() =>
    supabase!.from("session_types").update({ is_active: false }).eq("id", id),
  );
  if (error) throw SyncError.fromSupabase(error);
}

/** 수업유형 순서 변경 — sort_order 벌크 업데이트. */
export async function reorderSessionTypesRemote(
  items: { id: string; sortOrder: number }[],
): Promise<void> {
  if (!isSupabaseConfigured() || !supabase) {
    throw new Error("Supabase not configured");
  }
  const results = await Promise.all(
    items.map(({ id, sortOrder }) =>
      mutateWithAuthRetry(() =>
        supabase!.from("session_types").update({ sort_order: sortOrder }).eq("id", id),
      ),
    ),
  );
  if (results.some((r) => r.error)) throw new Error("Failed to reorder some session types");
}
